import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaFileAlt, FaLink } from 'react-icons/fa';
import { SOURCE_NAME } from '../constants/tagConstants';
import { detectLookupMode } from '../utils/lookupUtils';

function PaperLookup({ isOpen, onClose }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const mode = detectLookupMode(input);

  useEffect(() => {
    if (isOpen) {
      setInput('');
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    onClose();
    if (mode === 'id') {
      navigate(`/paper/${trimmed}`);
    } else {
      navigate(`/today?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const submitLabel = mode === 'id' ? 'Fetch' : 'Search';
  const submitColor = mode === 'id'
    ? 'bg-purple-600 hover:bg-purple-700'
    : 'bg-yellow-400 hover:bg-yellow-300 text-gray-900';
  const submitEnabled = mode !== 'empty';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-x-4 top-[12vh] z-[70] mx-auto max-w-xl"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              <div className="h-0.5 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-400" />

              <div className="p-6">
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="text-sm font-bold text-gray-900">
                    Find or fetch a paper
                  </h3>
                  <span className="text-[9px] text-gray-300 uppercase tracking-widest font-semibold">
                    Smart search
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  Paste an ID or URL to fetch a specific paper, or type keywords to search today's {SOURCE_NAME} digest.
                </p>

                {/* Dual-mode explainer cards */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <ModeCard
                    active={mode === 'id'}
                    dim={mode === 'keyword'}
                    icon={FaLink}
                    label="Direct fetch"
                    desc={`${SOURCE_NAME} ID, DOI, or URL`}
                    tone="purple"
                  />
                  <ModeCard
                    active={mode === 'keyword'}
                    dim={mode === 'id'}
                    icon={FaSearch}
                    label="Keyword search"
                    desc="Title, topic, or author"
                    tone="yellow"
                  />
                </div>

                <form onSubmit={handleSubmit}>
                  <div className={`relative transition-colors duration-200 rounded-xl border-2 ${
                    mode === 'id'
                      ? 'border-purple-300 bg-purple-50/40'
                      : mode === 'keyword'
                        ? 'border-yellow-300 bg-yellow-50/40'
                        : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300">
                      {mode === 'id' ? (
                        <FaLink className="text-xs text-purple-400" />
                      ) : mode === 'keyword' ? (
                        <FaSearch className="text-xs text-yellow-500" />
                      ) : (
                        <FaSearch className="text-xs" />
                      )}
                    </div>
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="e.g. 2401.12345, arxiv.org/abs/..., or 'diffusion models'"
                      className="w-full py-3 pl-10 pr-28 bg-transparent rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                    />
                    <button
                      type="submit"
                      disabled={!submitEnabled}
                      className={`absolute right-1.5 top-1/2 -translate-y-1/2 text-white rounded-lg px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${submitColor} ${!submitEnabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      {submitLabel}
                    </button>
                  </div>
                </form>

                {/* Inline mode hint */}
                <div className="mt-3 text-[11px] flex items-center gap-2 min-h-[16px]">
                  {mode === 'id' && (
                    <span className="inline-flex items-center gap-1.5 text-purple-600">
                      <FaFileAlt className="text-[9px]" />
                      Detected paper ID — we'll open the paper directly
                    </span>
                  )}
                  {mode === 'keyword' && (
                    <span className="inline-flex items-center gap-1.5 text-yellow-700">
                      <FaSearch className="text-[9px]" />
                      Keyword mode — we'll search today's papers
                    </span>
                  )}
                  {mode === 'empty' && (
                    <span className="text-gray-300">Start typing to pick a mode…</span>
                  )}
                </div>

                <p className="text-[10px] text-gray-300 mt-4 text-center">
                  <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">↵</kbd> to go
                  {' · '}
                  <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">Esc</kbd> to close
                  {' · '}
                  <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">⌘K</kbd> to reopen
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ModeCard({ active, dim, icon: Icon, label, desc, tone }) {
  const toneCls = tone === 'purple'
    ? {
        active: 'border-purple-400 bg-purple-50 text-purple-700',
        idle: 'border-gray-200 bg-white text-gray-600',
      }
    : {
        active: 'border-yellow-400 bg-yellow-50 text-yellow-700',
        idle: 'border-gray-200 bg-white text-gray-600',
      };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200 ${
      active ? toneCls.active : toneCls.idle
    } ${dim ? 'opacity-50' : ''}`}>
      <Icon className="text-xs flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider leading-tight">{label}</div>
        <div className="text-[9px] text-gray-400 leading-tight truncate">{desc}</div>
      </div>
    </div>
  );
}

export default PaperLookup;
